import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { requireActiveAuth as requireSupabaseAuth } from "@/lib/auth/requireActiveAuth";
import { captureMisconfiguredResource } from "@/lib/libraryMisconfig.server";

const TIER_RANK: Record<string, number> = {
  public: 0,
  library_member: 1,
  family_member: 2,
  legacy_member: 3,
};

const REQUIRED_RANK: Record<string, number> = {
  library: 1,
  family: 2,
  legacy: 3,
};

export type LibraryResourcePublic = {
  id: string;
  title: string;
  description: string | null;
  category: string;
  tier_required: string;
  cover_image_url: string | null;
  download_count: number;
  sort_order: number;
  has_file: boolean;
};

export const listPublishedResources = createServerFn({ method: "GET" }).handler(
  async (): Promise<LibraryResourcePublic[]> => {
    const { data, error } = await supabaseAdmin
      .from("family_library")
      .select(
        "id, title, description, category, tier_required, cover_image_url, thumbnail_url, download_count, sort_order, order_index, file_path",
      )
      .eq("published", true)
      .order("sort_order")
      .order("order_index");
    if (error) throw new Error(error.message);
    return (data ?? []).map((r) => ({
      id: r.id,
      title: r.title,
      description: r.description,
      category: r.category,
      tier_required: r.tier_required,
      cover_image_url: r.cover_image_url ?? r.thumbnail_url ?? null,
      download_count: r.download_count,
      sort_order: r.sort_order ?? r.order_index ?? 0,
      has_file: Boolean(r.file_path),
    }));
  },
);

export const getMyLibraryTier = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { userId } = context;
    const { data, error } = await supabaseAdmin.rpc("client_tier", {
      p_user_id: userId,
    });
    if (error) throw new Error(error.message);
    const { data: staffStr, error: staffErr } = await supabaseAdmin.rpc("staff_tier", {
      p_user_id: userId,
    });
    if (staffErr) throw new Error(staffErr.message);
    // tier = genuine membership; isStaff = separate firm-access axis.
    return { tier: (data as string) ?? "public", isStaff: (staffStr as string) !== "none" };
  });

export const requestLibraryDownload = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) =>
    z.object({ id: z.string().uuid() }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { userId } = context;

    const { data: row, error } = await supabaseAdmin
      .from("family_library")
      .select("id, file_path, tier_required, published, title")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row || !row.published) throw new Error("Resource not available");

    const { data: tierStr, error: tierErr } = await supabaseAdmin.rpc(
      "client_tier",
      { p_user_id: userId },
    );
    if (tierErr) throw new Error(tierErr.message);
    // Membership tier is the household's real tier. Staff access is a separate
    // axis: firm roles may open any resource for review regardless of tier.
    const { data: staffStr, error: staffErr } = await supabaseAdmin.rpc(
      "staff_tier",
      { p_user_id: userId },
    );
    if (staffErr) throw new Error(staffErr.message);
    const isStaff = (staffStr as string) !== "none";
    const userRank = isStaff ? 99 : TIER_RANK[tierStr as string] ?? 0;
    const needRank = REQUIRED_RANK[row.tier_required] ?? 1;
    if (userRank < needRank) {
      throw new Error("Membership required to download this resource");
    }

    if (!row.file_path) {
      captureMisconfiguredResource(row.id, row.title);
      throw new Error("This resource is not yet available.");
    }
    const { data: signed, error: signErr } = await supabaseAdmin.storage
      .from("family-library-files")
      .createSignedUrl(row.file_path, 60 * 60);
    if (signErr || !signed?.signedUrl) {
      throw new Error(signErr?.message ?? "Could not generate download URL");
    }
    const url = signed.signedUrl;

    await supabaseAdmin.rpc("increment_library_download", { _id: row.id });
    await supabaseAdmin.from("activity_logs").insert({
      user_id: userId,
      action: "family_library.downloaded",
      target_type: "family_library",
      target_id: row.id,
      metadata: { title: row.title, tier_required: row.tier_required },
    });

    return { url };
  });

// ---------- Admin ----------

async function assertAdmin(userId: string) {
  const { data, error } = await supabaseAdmin.rpc("is_admin", {
    _user_id: userId,
  });
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden");
}

export const adminListResources = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);
    const { data, error } = await supabaseAdmin
      .from("family_library")
      .select("*")
      .order("sort_order")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

const upsertSchema = z.object({
  id: z.string().uuid().optional(),
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2000).nullable().optional(),
  category: z.enum(["workbook", "checklist", "template", "guide"]),
  tier_required: z.enum(["library", "family", "legacy"]),
  file_path: z.string().trim().max(500).nullable().optional(),
  cover_image_url: z.string().trim().url().max(1000).nullable().optional(),
  sort_order: z.number().int().min(0).max(9999).default(0),
  published: z.boolean().default(false),
  is_lead_magnet: z.boolean().default(false),
  lead_magnet_headline: z.string().trim().max(160).nullable().optional(),
  lead_magnet_subhead: z.string().trim().max(400).nullable().optional(),
});

export const adminUpsertResource = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => upsertSchema.parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const payload = {
      title: data.title,
      description: data.description ?? null,
      category: data.category,
      tier_required: data.tier_required,
      file_path: data.file_path ?? null,
      cover_image_url: data.cover_image_url ?? null,
      sort_order: data.sort_order,
      published: data.published,
      is_lead_magnet: data.is_lead_magnet,
      lead_magnet_headline: data.lead_magnet_headline || null,
      lead_magnet_subhead: data.lead_magnet_subhead || null,
      audience: "all",
      created_by: context.userId,
    };
    let resultId = data.id;
    if (data.id) {
      const { error } = await supabaseAdmin
        .from("family_library")
        .update(payload)
        .eq("id", data.id);
      if (error) throw new Error(error.message);
    } else {
      const { data: ins, error } = await supabaseAdmin
        .from("family_library")
        .insert(payload)
        .select("id")
        .single();
      if (error) throw new Error(error.message);
      resultId = ins.id;
    }
    // Only one featured lead magnet at a time.
    if (data.is_lead_magnet && resultId) {
      await supabaseAdmin
        .from("family_library")
        .update({ is_lead_magnet: false })
        .neq("id", resultId)
        .eq("is_lead_magnet", true);
    }
    await supabaseAdmin.from("activity_logs").insert({
      user_id: context.userId,
      action: data.id ? "family_library.updated" : "family_library.created",
      target_type: "family_library",
      target_id: resultId,
      metadata: { title: data.title },
    });
    return { id: resultId };
  });

export const adminTogglePublished = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string; published: boolean }) =>
    z.object({ id: z.string().uuid(), published: z.boolean() }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { error } = await supabaseAdmin
      .from("family_library")
      .update({ published: data.published })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    await supabaseAdmin.from("activity_logs").insert({
      user_id: context.userId,
      action: "family_library.updated",
      target_type: "family_library",
      target_id: data.id,
      metadata: { published: data.published },
    });
    return { ok: true };
  });

export const adminDeleteResource = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) =>
    z.object({ id: z.string().uuid() }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { data: row } = await supabaseAdmin
      .from("family_library")
      .select("file_path, title")
      .eq("id", data.id)
      .maybeSingle();
    if (row?.file_path) {
      await supabaseAdmin.storage
        .from("family-library-files")
        .remove([row.file_path]);
    }
    const { error } = await supabaseAdmin
      .from("family_library")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    await supabaseAdmin.from("activity_logs").insert({
      user_id: context.userId,
      action: "family_library.deleted",
      target_type: "family_library",
      target_id: data.id,
      metadata: { title: row?.title },
    });
    return { ok: true };
  });

export const adminSignedUploadUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { filename: string }) =>
    z
      .object({ filename: z.string().trim().min(1).max(255) })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const safe = data.filename.replace(/[^a-zA-Z0-9._-]/g, "_");
    const path = `${crypto.randomUUID()}/${safe}`;
    const { data: signed, error } = await supabaseAdmin.storage
      .from("family-library-files")
      .createSignedUploadUrl(path);
    if (error || !signed) throw new Error(error?.message ?? "Upload URL failed");
    return { path, token: signed.token, signedUrl: signed.signedUrl };
  });